import React from 'react';
import type {
  ProgramSlideTableBlockV1,
  ProgramSlideTableVerticalAlignV1,
} from '../../types/programContentSlideCanvas';
import { slideTextFontCssFamily } from '../../types/programContentSlideCanvas';
import {
  getTableAnchorCell,
  getTableAnchorSelectionVisual,
  isTableCellCovered,
  type TableSelectionRect,
} from '../../utils/programSlideTableBlock';
import {
  measureTableContentHeightPx,
  measureTableRowHeightsPx,
  syncTableHeightToContent,
} from '../../utils/programSlideTableAutoHeight';

export type ProgramSlideTableBlockViewProps = {
  table: ProgramSlideTableBlockV1;
  readOnly: boolean;
  isTableSelected: boolean;
  layerZIndex: number;
  selection: TableSelectionRect | null;
  editingCellId: string | null;
  canvasHeightPx?: number;
  blockRef?: (el: HTMLDivElement | null) => void;
  onAutoResizeTable?: (table: ProgramSlideTableBlockV1) => void;
  onTableLayoutChange?: () => void;
  onBorderPointerDown: (e: React.PointerEvent) => void;
  onCellPointerDown: (e: React.PointerEvent, row: number, col: number, cellId: string) => void;
  onCellDoubleClick: (e: React.MouseEvent, row: number, col: number, cellId: string) => void;
  onCellTextChange: (cellId: string, text: string) => void;
  onCellEditBlur: (cellId: string) => void;
};

function verticalAlignToCss(align: ProgramSlideTableVerticalAlignV1 | undefined): string {
  if (align === 'top') return 'top';
  if (align === 'bottom') return 'bottom';
  return 'middle';
}

function verticalAlignInnerClass(align: ProgramSlideTableVerticalAlignV1 | undefined): string {
  if (align === 'top') return 'cp-program-slide-table-cell-inner--v-top';
  if (align === 'bottom') return 'cp-program-slide-table-cell-inner--v-bottom';
  return 'cp-program-slide-table-cell-inner--v-middle';
}

const TableCellTextarea: React.FC<{
  value: string;
  verticalAlign: ProgramSlideTableVerticalAlignV1 | undefined;
  onChange: (value: string) => void;
  onBlur: () => void;
}> = ({ value, verticalAlign, onChange, onBlur }) => {
  const ref = React.useRef<HTMLTextAreaElement | null>(null);
  const vAlign = verticalAlign ?? 'middle';

  const syncHeightAndScroll = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0';
    const scrollHeight = el.scrollHeight;
    el.style.height = `${scrollHeight}px`;
    el.style.overflowY = 'hidden';
    if (vAlign === 'bottom') {
      el.scrollTop = el.scrollHeight;
    } else if (vAlign === 'middle') {
      el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
    } else {
      el.scrollTop = 0;
    }
  }, [vAlign]);

  React.useLayoutEffect(() => {
    syncHeightAndScroll();
  }, [value, vAlign, syncHeightAndScroll]);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const focus = () => {
      el.focus({ preventScroll: true });
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    };
    focus();
    window.requestAnimationFrame(focus);
  }, []);

  return (
    <textarea
      ref={ref}
      className="cp-program-slide-table-cell-input"
      value={value}
      rows={1}
      onChange={(e) => {
        onChange(e.target.value);
        requestAnimationFrame(syncHeightAndScroll);
      }}
      onBlur={onBlur}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.currentTarget.blur();
        }
        e.stopPropagation();
      }}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
};

const ProgramSlideTableBlockView: React.FC<ProgramSlideTableBlockViewProps> = ({
  table,
  readOnly,
  isTableSelected,
  layerZIndex,
  selection,
  editingCellId,
  canvasHeightPx = 0,
  blockRef,
  onAutoResizeTable,
  onTableLayoutChange,
  onBorderPointerDown,
  onCellPointerDown,
  onCellDoubleClick,
  onCellTextChange,
  onCellEditBlur,
}) => {
  const innerBlockRef = React.useRef<HTMLDivElement | null>(null);
  const gridRef = React.useRef<HTMLTableElement | null>(null);
  const measureRafRef = React.useRef<number | null>(null);

  const scheduleAutoHeightMeasure = React.useCallback(() => {
    if (!onAutoResizeTable || canvasHeightPx <= 0) return;
    if (measureRafRef.current != null) {
      window.cancelAnimationFrame(measureRafRef.current);
    }
    measureRafRef.current = window.requestAnimationFrame(() => {
      measureRafRef.current = null;
      const blockEl = innerBlockRef.current;
      const gridEl = gridRef.current;
      if (!blockEl || !gridEl) return;
      const rowHeightsPx = measureTableRowHeightsPx(gridEl);
      const contentHeightPx = measureTableContentHeightPx(gridEl);
      const next = syncTableHeightToContent(table, contentHeightPx, rowHeightsPx, canvasHeightPx);
      if (next) {
        onAutoResizeTable(next);
        onTableLayoutChange?.();
      }
    });
  }, [canvasHeightPx, onAutoResizeTable, onTableLayoutChange, table]);

  React.useLayoutEffect(() => {
    scheduleAutoHeightMeasure();
  }, [scheduleAutoHeightMeasure]);

  React.useLayoutEffect(() => {
    const blockEl = innerBlockRef.current;
    const gridEl = gridRef.current;
    if (!blockEl) return;
    const observer = new ResizeObserver(() => scheduleAutoHeightMeasure());
    observer.observe(blockEl);
    if (gridEl) observer.observe(gridEl);
    return () => {
      observer.disconnect();
      if (measureRafRef.current != null) {
        window.cancelAnimationFrame(measureRafRef.current);
        measureRafRef.current = null;
      }
    };
  }, [scheduleAutoHeightMeasure]);

  const setBlockRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      innerBlockRef.current = el;
      blockRef?.(el);
    },
    [blockRef]
  );

  const borderWidth = Math.max(0, table.borderWidthPx ?? 1);
  const borderColor = table.borderColorHex?.match(/^#/) ? table.borderColorHex : '#333333';
  const fontSize = table.defaultFontSizePx ?? 14;
  const fontFamily = slideTextFontCssFamily(table.defaultFontFamily);

  const rows: React.ReactNode[] = [];
  for (let row = 0; row < table.rowCount; row += 1) {
    const cells: React.ReactNode[] = [];
    for (let col = 0; col < table.colCount; col += 1) {
      if (isTableCellCovered(table, row, col)) continue;
      const anchor = getTableAnchorCell(table, row, col);
      if (!anchor) continue;
      const { cell, anchorRow, anchorCol } = anchor;
      const isEditing = editingCellId === cell.id;
      const selectionVisual =
        isTableSelected && selection
          ? getTableAnchorSelectionVisual(table, anchorRow, anchorCol, cell.rowSpan, cell.colSpan, selection)
          : null;
      const colWidthPct =
        table.colWidthsFrac.slice(anchorCol, anchorCol + cell.colSpan).reduce((a, b) => a + b, 0) * 100;
      const vAlign = cell.verticalAlign ?? 'middle';

      cells.push(
        <td
          key={cell.id}
          data-table-cell-id={cell.id}
          data-table-cell-row={anchorRow}
          data-table-cell-col={anchorCol}
          rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
          colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}
          className={[
            'cp-program-slide-table-cell',
            selectionVisual?.selectedSingle
              ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-single'
              : '',
            selectionVisual?.selectedMulti
              ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi'
              : '',
            selectionVisual?.selectedMulti && selectionVisual.edgeTop
              ? 'cp-base-timetable-cell-edge-top'
              : '',
            selectionVisual?.selectedMulti && selectionVisual.edgeRight
              ? 'cp-base-timetable-cell-edge-right'
              : '',
            selectionVisual?.selectedMulti && selectionVisual.edgeBottom
              ? 'cp-base-timetable-cell-edge-bottom'
              : '',
            selectionVisual?.selectedMulti && selectionVisual.edgeLeft
              ? 'cp-base-timetable-cell-edge-left'
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{
            width: `${colWidthPct}%`,
            textAlign: cell.textAlign,
            verticalAlign: verticalAlignToCss(vAlign),
            color: cell.colorHex,
            backgroundColor: cell.backgroundColorHex,
            fontWeight: cell.bold ? 700 : 400,
            fontSize: `${fontSize}px`,
            fontFamily,
            border: `${borderWidth}px solid ${borderColor}`,
          }}
          onPointerDown={(e) => {
            if (readOnly) return;
            e.stopPropagation();
            onCellPointerDown(e, anchorRow, anchorCol, cell.id);
          }}
          onDoubleClick={(e) => {
            if (readOnly) return;
            e.stopPropagation();
            onCellDoubleClick(e, anchorRow, anchorCol, cell.id);
          }}
        >
          <div className={'cp-program-slide-table-cell-inner ' + verticalAlignInnerClass(vAlign)}>
            {isEditing ? (
              <TableCellTextarea
                value={cell.text}
                verticalAlign={vAlign}
                onChange={(text) => onCellTextChange(cell.id, text)}
                onBlur={() => onCellEditBlur(cell.id)}
              />
            ) : (
              <span className="cp-program-slide-table-cell-text">{cell.text || '\u00a0'}</span>
            )}
          </div>
        </td>
      );
    }
    rows.push(<tr key={`row-${row}`}>{cells}</tr>);
  }

  return (
    <div
      ref={setBlockRef}
      className={'cp-program-slide-canvas-table' + (isTableSelected ? ' is-selected' : '')}
      style={{
        left: `${table.x * 100}%`,
        top: `${table.y * 100}%`,
        width: `${table.w * 100}%`,
        height: 'auto',
        zIndex: layerZIndex,
      }}
    >
      <table ref={gridRef} className="cp-program-slide-table-grid">
        <tbody>{rows}</tbody>
      </table>
      {!readOnly ? (
        <>
          <div
            className="cp-program-slide-table-drag-edge cp-program-slide-table-drag-edge--top"
            onPointerDown={onBorderPointerDown}
            aria-hidden
          />
          <div
            className="cp-program-slide-table-drag-edge cp-program-slide-table-drag-edge--right"
            onPointerDown={onBorderPointerDown}
            aria-hidden
          />
          <div
            className="cp-program-slide-table-drag-edge cp-program-slide-table-drag-edge--bottom"
            onPointerDown={onBorderPointerDown}
            aria-hidden
          />
          <div
            className="cp-program-slide-table-drag-edge cp-program-slide-table-drag-edge--left"
            onPointerDown={onBorderPointerDown}
            aria-hidden
          />
        </>
      ) : null}
    </div>
  );
};

export default ProgramSlideTableBlockView;
